/**
 * Leads Service — Types.
 *
 * Mirrors the shipped backend DTOs in `backend/src/leads/` exactly — field
 * names and enum values are copied from `LeadBoardQueryDto`,
 * `LeadBoardResponseDto` and `LeadBoardRowDto`, not from the spec prose. Two
 * places where the spec and the shipped code disagree, resolved in favour of
 * the code (root `CLAUDE.md`: "Do not transform data from the backend"):
 *
 * - `technical-spec.md` §5 lists the people filter as `owner[]`; the shipped
 *   query DTO has **two independent people filters** — `solicitorId` (the
 *   solicitor alone) and `ownerId` (the intake owner, reinstated in LF-6099
 *   after LF-6054 retired it). The middle spelling `teamMemberId` — LF-6015's
 *   single parameter matching owner *or* solicitor — stays retired and
 *   rejected: two named filters are what replaced it, and a union of the two
 *   people a lead has is not a filter anyone can read a meaning off.
 * - The same table lists a `source` filter without naming its values; they are
 *   the `LeadBusinessSource` members below.
 *
 * String-literal unions rather than TS enums, matching every other service
 * here (`title-searches`, `intake-forms`).
 */

import type {
  DocumentType,
  SpeInviteFailureReason
} from '@/lib/services/templates';

import type { EmailType } from '@/lib/services/email-templates/types';
import type { IntakeFormStatus } from '@/lib/services/intake-forms/types';
import type { PostalAddress } from '@/lib/services/matters/types';

// =============================================================================
// Enums (mirrors of `backend/src/leads/enums/*`)
// =============================================================================

/**
 * The seven **stored** stages (`leads.stage`). `ready_to_book` is deliberately
 * absent — it is derived, never written.
 */
export type LeadStage =
  | 'new_enquiry'
  | 'form_sent'
  | 'booked'
  | 'meeting_held'
  | 'cost_agreement_sent'
  | 'cost_agreement_signed'
  | 'initial_fee_paid';

/** The one derived rung, produced by `LeadStageService`, never persisted. */
export type LeadDerivedStage = 'ready_to_book';

/** What a lead's stage reads as on the board — stored stages plus the derived rung. */
export type LeadEffectiveStage = LeadStage | LeadDerivedStage;

/** The three sections of the In-progress tab. `null` once a lead has an outcome. */
export type LeadSection =
  | 'new_enquiry'
  /**
   * Somebody handed the enquiry over and nobody has picked it up yet.
   *
   * **One entry and one exit, both deliberate acts**: in on `Ready for
   * allocation`, out when the enquiry has a matter whose acting solicitor is
   * set — the allocator's own single gesture of creating the matter and naming
   * who will run it. The stage plays no part, so booking a meeting neither
   * enters nor leaves the band.
   *
   * **Unlike the other three, this section is conditional**: the board omits it
   * from the response entirely unless the firm has named an allocator in
   * `configuration.leads.allocation.handoverUserIds`. That is what lets the
   * client tell *absent because unconfigured* (render nothing) from *present but
   * empty* (render the header and `Nothing here.`) — so never collapse the two
   * by hiding a section on row count.
   */
  | 'pending_allocation'
  | 'meeting_booked'
  | 'pending_engagement';

/** The terminal band stored on `leads.outcome`. `null` means still in progress. */
export type LeadOutcome = 'proceeding' | 'not_proceeding';

/**
 * Which band of the board to read. One filter, not two — the three tabs
 * partition purely on outcome. `open` is `outcome IS NULL`, which is why it is
 * a separate union from `LeadOutcome`: a query parameter cannot carry
 * "explicitly null" unambiguously.
 */
export type LeadBoardOutcomeFilter = 'open' | 'proceeding' | 'not_proceeding';

/** The seven fixed reasons a lead is closed as not proceeding. */
export type LeadNotProceedingReason =
  | 'went_elsewhere'
  | 'cost'
  | 'no_response'
  | 'not_proceeding_yet'
  | 'no_estate_to_administer'
  | 'conflict'
  | 'out_of_scope';

/**
 * Where the enquiry came from — the *channel*. Deliberately separate from
 * `referredBy` (free text), which is the *person*.
 */
export type LeadBusinessSource =
  | 'phone'
  | 'web_form'
  | 'accountant'
  | 'financial_adviser'
  | 'existing_client'
  | 'wills_register'
  | 'funeral_director'
  | 'word_of_mouth';

/** Where the cost agreement stands, derived from the artefact. */
export type LeadCostAgreementState =
  | 'none'
  | 'awaiting_signature'
  | 'awaiting_fee'
  | 'paid';

/** What the firm is still waiting on from the client. Advisory only. */
export type LeadOutstandingItemType = 'death_certificate' | 'will' | 'other';

/** A yes/no fact that is genuinely unknown until somebody asks. */
export type LeadTriState = 'yes' | 'no' | 'unknown';

/** An LPR's role, on the matter's own vocabulary (`LEGAL_PERSONAL_REPRESENTATIVE_ROLES`). */
export type LprRole = 'executor' | 'applicant_for_letters_of_administration';

/** A person on an enquiry, as a firm contact (VALE-2061). */
export interface LeadContact {
  /** The enquiry contact row, what the edit and remove routes take. */
  id: number;
  contactId: number;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  organisationName: string | null;
  /** The composed display name. */
  name: string;
  email: string | null;
  phone: string | null;
  address: PostalAddress | null;
  /** Whether they are one of the estate's legal personal representatives. */
  isLegalPersonalRepresentative: boolean;
  /** `null` is a role not yet known, or not an LPR. */
  legalPersonalRepresentativeRole: LprRole | null;
  relationshipToDeceased: string | null;
}

/** An executor the enquiry's intake form names who is not on the enquiry yet. */
export interface LeadNamedExecutor {
  id: string;
  /** As the form recorded it: one free-text name. */
  fullName: string;
  email: string | null;
  phone: string | null;
  relationshipToDeceased: string | null;
}

/** What an enquiry holds about one of its people. */
interface LeadContactFacts {
  isLegalPersonalRepresentative?: boolean;
  legalPersonalRepresentativeRole?: LprRole | null;
  relationshipToDeceased?: string | null;
}

/** `POST /leads/:id/contacts`: link a firm contact (created or found through the contact drawer). */
export interface AddLeadContactInput extends LeadContactFacts {
  contactId: number;
}

/** `PATCH /leads/:id/contacts/:leadContactId`: the person's own details are edited on their contact. */
export type UpdateLeadContactInput = LeadContactFacts;

/** How the appointment is taken. `link` is only meaningful for `video`. */
export type LeadMeetingPreference = 'in_person' | 'video';

/** Whether the matter was created from the lead or an existing one was linked. */
export type LeadMatterLinkSource = 'created' | 'associated';

/** Where the cost agreement came from. */
export type LeadCostAgreementSource = 'precedent' | 'upload';

/** The append-only activity log's vocabulary (`lead_events.type`). */
export type LeadEventType =
  | 'enquiry_captured'
  /**
   * The intake invitation was sent. `data.recipientNames` names who it went to
   * (VALE-2060); rows written before that carry none.
   */
  | 'form_sent'
  /**
   * The outstanding form was re-addressed to somebody else (VALE-2060).
   * `data`: `{ intakeFormId, fromName, toName }`. Restarts the reminder count.
   */
  | 'form_redirected'
  | 'chase_sent'
  | 'form_opened'
  | 'form_completed'
  | 'meeting_recorded'
  | 'meeting_held'
  | 'confirmation_sent'
  | 'brief_sent'
  | 'reply_received'
  | 'reply_read'
  | 'recording_added'
  | 'ca_added'
  | 'ca_sent'
  | 'ca_signed'
  | 'fee_recorded'
  | 'matter_linked'
  /** The firm's own conflict check was marked done or not done. */
  | 'conflict_check_recorded'
  /** An uploaded death certificate was recognised and cleared its outstanding item. */
  | 'death_certificate_recognised'
  /**
   * The client added documents to the intake form after submitting it, one entry
   * per sitting. `data`: `{ intakeFormId, count, filenames }`.
   */
  | 'client_documents_added'
  /**
   * Somebody handed the enquiry over to be allocated.
   *
   * **This pair is the whole of the hand-over state** — there is no column for
   * it. Latest-wins between `allocation_requested` and `allocation_withdrawn`
   * settles whether the enquiry is waiting on its allocator, exactly as
   * `closed` / `reopened` settles the outcome. The server resolves it into
   * `LeadDetail.allocation.requestedAt`; never re-derive it from the events here.
   */
  | 'allocation_requested'
  /** The hand-over was taken back before anybody picked the enquiry up. */
  | 'allocation_withdrawn'
  /** The configured allocator(s) were emailed that the enquiry is ready to allocate. */
  | 'allocation_notified'
  | 'closed'
  | 'reopened'
  | 'stage_overridden';

// =============================================================================
// Board read — `GET /leads`
// =============================================================================

/**
 * The board read's query surface — `LeadBoardQueryDto`, and nothing beyond it.
 *
 * There is deliberately **no sort parameter**: ordering is derived server-side
 * from what each row needs, and every caller inherits it.
 */
export interface LeadBoardFilters {
  /** Defaults to `open` server-side. */
  outcome?: LeadBoardOutcomeFilter;
  /**
   * Restrict to leads these solicitors are on: matched server-side against the
   * lead's **preferred solicitor OR the booked solicitor of one of its
   * meetings**, and deliberately never against whoever took the enquiry — that
   * is `ownerId` below, a **separate** filter. Serialised as a repeated
   * parameter.
   *
   * The retired spelling `teamMemberId` is **rejected with 400** by the backend
   * rather than ignored, so this name and the server's must move together.
   */
  solicitorId?: number[];
  /**
   * Restrict to leads these people **took** — the intake owner (LF-6099,
   * reinstating what LF-6054 removed). Serialised as a repeated parameter.
   *
   * **Independent of `solicitorId`, not a widening of it.** The two answer
   * different questions — *who is the client seeing?* and *who took this call?*
   * — and the customer evidence is that a coordinator routes on the second
   * (their own intake form asks it as a required field). Combined, the backend
   * intersects them, which is what makes "my enquiries with Sarah" expressible.
   */
  ownerId?: number[];
  /** Restrict to these business sources. Serialised as a repeated parameter. */
  source?: LeadBusinessSource[];
  /** Only leads whose enquirer replied and nobody has opened the thread since. */
  unread?: boolean;
  /** Only leads carrying the board's manual flag. */
  flagged?: boolean;
  /**
   * Only leads chased to the end of the firm's cadence with the grace elapsed
   * and nothing back — the row's bold-amber Age. Derived, like `unread`, so a
   * terminal `outcome` legitimately returns an empty page.
   */
  stalled?: boolean;
  /**
   * Free text over deceased name, client names, referrer and the name of whoever
   * took the enquiry.
   */
  q?: string;
  /** Terminal tabs only — the In-progress board returns every open lead. */
  page?: number;
  /** Terminal tabs only. Server default 25, maximum 100. */
  limit?: number;
}

/**
 * Just enough of a user to render `TeamAvatars` — never the whole user.
 *
 * **Both names are nullable and the email is not.** An invited user who has not
 * accepted yet has neither name (`law_firm_users.first_name`/`last_name` are
 * NULL), which is why these are typed honestly here and why the DTO ships the
 * email: `userDisplayName` falls back to it rather than interpolating the nulls
 * into the literal string `null null` (LF-6015).
 */
export interface LeadBoardUser {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

/**
 * The longest-standing open item, which is the only one the row shows —
 * `+N` covers the rest.
 */
export interface LeadBoardOutstanding {
  type: LeadOutstandingItemType;
  /** ISO-8601 instant. */
  requestedAt: string;
  note: string | null;
}

/**
 * One row of the board, with **every derived fact already resolved**
 * server-side. The frontend renders this and re-derives none of it — the
 * single most important structural rule in the module.
 *
 * Copy *is* the frontend's: the row's title rule ("Estate of {deceasedName}",
 * falling back to "Enquiry from {clientNames}") and the compressed meeting
 * format are rendering, so this carries the raw inputs. What must not be
 * re-derived is **state** — section, stall, effective stage, cost-agreement
 * standing all arrive settled.
 *
 * Date fields are `string` rather than `Date`: JSON carries ISO-8601 text and
 * nothing revives it, so typing them as `Date` would be a lie the compiler
 * would then enforce downstream.
 */
export interface LeadBoardRow {
  id: number;

  // ── Identity ────────────────────────────────────────────────────────────
  /** Drives the `Estate of …` title when present. */
  deceasedName: string | null;
  /** The enquirer(s) — the title falls back to these when no deceased is known. */
  clientNames: string;
  /** Matter-type slug, for the row's type icon. */
  enquiryType: string;
  matterId: number | null;
  /** The linked matter's reference, for the square `#REF` chip. */
  matterReference: string | null;
  /** The board's one manual mark. It deliberately does NOT reorder the row. */
  flagged: boolean;

  // ── Derived state (never recomputed client-side) ────────────────────────
  effectiveStage: LeadEffectiveStage;
  stage: LeadStage;
  /** `null` once the lead has an outcome — it has left the In-progress tab. */
  section: LeadSection | null;
  /** Whole elapsed days since the stage changed. Never negative. */
  daysInStage: number;
  /** Chased to the end of the firm's cadence, grace elapsed, nothing back. */
  isStalled: boolean;
  /** The enquirer has replied at all, read or not. */
  hasInboundReply: boolean;
  /** Replied, and nobody has opened the thread since. */
  hasUnseenReply: boolean;
  /** The client added documents to the submitted intake form, and nobody has looked at the enquiry's documents since. */
  hasUnseenClientDocuments: boolean;
  costAgreementState: LeadCostAgreementState;
  /** A restatement of `effectiveStage === 'ready_to_book'`. No badge on the row, and no ordering. */
  isReadyToBook: boolean;

  // ── Form column ─────────────────────────────────────────────────────────
  /** `null` means no form — the row renders the dashed `Send` pill. */
  formSentAt: string | null;
  formCompletedAt: string | null;
  /**
   * Completion percentage, or `null` when the figure genuinely does not exist
   * yet — never a fabricated `0` the Form column would render as fact.
   */
  formProgress: number | null;
  /** Chases actually sent, for the Form column's bell. */
  chaseCount: number;

  // ── Meeting column ──────────────────────────────────────────────────────
  /** The next upcoming meeting, else the most recent past one. */
  nextMeetingAt: string | null;
  /** True when `nextMeetingAt` is in the past (the cell reads `Met 26/7`). */
  nextMeetingIsPast: boolean;

  // ── Remaining row signals ───────────────────────────────────────────────
  outstanding: LeadBoardOutstanding | null;
  /** Total open outstanding items, for the `+N` suffix. */
  outstandingCount: number;
  /** A Scribe recording is attached to one of the lead's meetings. */
  hasRecording: boolean;
  outcome: LeadOutcome | null;
  notProceedingReason: LeadNotProceedingReason | null;
  businessSource: LeadBusinessSource | null;
  referredBy: string | null;
  /** The estate's value in **cents**. Never a float. */
  netAssetValueCents: number | null;
  /**
   * The lead's **shared notes** — the same free text the drawer's notes box
   * holds (`leads.more_info`).
   *
   * It is on the row because the row renders it as its grey secondary note: what
   * a colleague wrote about the enquiry is more use to somebody scanning the
   * board than where the enquiry came from, so the note takes that slot and the
   * business source falls back to it only when there is no note. Arrives whole
   * and untruncated — how much fits is the row's problem.
   */
  moreInfo: string | null;
  /**
   * Who took the enquiry — the **intake owner** (LF-6099).
   *
   * **The board still renders no column for it**, which is LF-6054's strongest
   * UX finding and survives that ticket's partial reversal: the one person a row
   * needs to name is the solicitor the client is seeing. The owner is reachable
   * three other ways — the toolbar's `Intake owner` filter, the drawer's
   * editable field, and the free-text search.
   */
  owner: LeadBoardUser;
  /**
   * The solicitor the enquirer is seeing or is due to see. §3.5.13's column.
   *
   * Already resolved server-side to the **rendered meeting's booked solicitor**,
   * falling back to the lead's preferred solicitor, because that choice depends
   * on which meeting the product renders and this surface re-derives nothing.
   * `null` means nobody has been decided yet, which the row says in words.
   */
  solicitor: LeadBoardUser | null;

  // ── Instants ────────────────────────────────────────────────────────────
  enquiryAt: string;
  stageChangedAt: string;
  /** The terminal tabs' recency key. `null` sinks rather than being defaulted. */
  closedAt: string | null;
}

/**
 * One section of the In-progress tab, rows already in that section's order.
 *
 * **All three sections are always present, even when empty**, because empty is
 * information: unfiltered, an empty section shows `Nothing here.`; under an
 * active filter it disappears entirely. Omitting empty sections would make
 * those two states indistinguishable.
 */
export interface LeadBoardSection {
  section: LeadSection;
  rows: LeadBoardRow[];
}

/** A terminal tab's page — most recently closed first, 25 at a time. */
export interface LeadBoardPage {
  items: LeadBoardRow[];
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
}

/**
 * The board read's response — **deliberately bimodal**. The In-progress tab is
 * a pipeline (three sections, each with its own ordering, unpaginated); a
 * terminal tab is a log (flat, recency-ordered, paginated).
 *
 * Exactly one of `sections` / `page` is populated, decided by the `outcome`
 * filter that produced the response and echoed back on it. Branch on
 * `outcome`, or use the `isOpenBoard` guard in `./client`.
 */
export interface LeadBoardResponse {
  outcome: LeadBoardOutcomeFilter;
  /** The three In-progress sections in board order. `null` on a terminal tab. */
  sections: LeadBoardSection[] | null;
  /** The terminal tab's page. `null` on the In-progress tab. */
  page: LeadBoardPage | null;
}

// =============================================================================
// Capture — `POST /leads`
// =============================================================================

/**
 * Capture a new enquiry — `CreateLeadDto`, and nothing beyond it (LF-5987 /
 * LEADS-20, `functional-spec.md` §5.1).
 *
 * **`clientNames` is the only required field**, which is the whole point of the
 * surface: the coordinator is typing live on a phone call, and a form that
 * demanded more risks her abandoning it mid-call and losing the enquiry
 * altogether. Every other field resting unset is a correct lead, not an
 * incomplete one.
 *
 * One absence is deliberate and must not be "helpfully" added here:
 * `deceasedTitle`, rarely known on a first call — the send dialog asks at the
 * moment it changes the outgoing email ("your late father" vs "your late
 * mother"). `PATCH /leads/:id` sets it in the meantime.
 *
 * **`email`, `address` and `otherPartyDetails` are asked here** (LF-6099): they
 * were the intake coordinator's first three unprompted requests in the
 * validation session, all three known on the call and all three costing a
 * phone call to recover later. Every one is optional, so `clientNames`-only
 * capture is untouched.
 *
 * `ownerUserId` is optional and the capture modal sends it **only when the
 * coordinator changed it** (LF-6099): the backend defaults it to the
 * authenticated caller, who is usually the person who took the call, so an
 * unchanged picker leaves the key off and the server's own default stands.
 */
export interface CaptureLeadInput {
  /** "Who's enquiring". Trimmed server-side; whitespace-only is a 400. */
  clientFirstName: string;
  clientMiddleName?: string;
  /**
   * Optional, unlike on the firm's own paper enquiry form.
   *
   * Requiring it would amend R1.1, and the coordinator who catches only a first
   * name before the caller rings off still has to be able to save the enquiry.
   */
  clientLastName?: string;
  /**
   * How the enquirer is actually addressed — "Bob" for a Robert. Supersedes the
   * legal name in every client-facing enquiry email's greeting, and is carried
   * onto the contact at conversion.
   */
  clientPreferredName?: string;
  /** The optional second enquirer — the spouse or co-executor on the same call. */
  client2FirstName?: string;
  client2MiddleName?: string;
  client2LastName?: string;
  phone?: string;
  /** The enquirer's email — what the intake-form send prefills from (§5.2). */
  email?: string;
  /** The enquirer's **home** address — which office is theirs depends on it. */
  address?: PostalAddress;
  /**
   * The deceased, in the three parts the created matter's `decedent*` columns
   * take. The middle name is the part the free-text capture could never hold,
   * and it is the one a grant document names in full.
   */
  deceasedFirstName?: string;
  deceasedMiddleName?: string;
  deceasedLastName?: string;
  /** The enquirer's relationship **to** the deceased. */
  relationshipToDeceased?: string;
  /**
   * The other executors and any other party to the estate — free text, feeding
   * the firm's conflict check, which is run for **all** of them.
   */
  otherPartyDetails?: string;
  /**
   * The intake coordinator's two estate ticks, asked on the first call
   * (VALE-988). Tri-state and optional for the reason the columns are tri-state:
   * `unknown` is the unasked state, so omitting them lands on the same value the
   * column defaults to and R1.1's name-only capture is untouched.
   *
   * Estate administration only — `buildCaptureInput` drops them for any other
   * enquiry type, exactly as it drops the deceased's name.
   */
  firmHoldsWill?: LeadTriState;
  /**
   * Where the original will is held, when the firm does not hold it: free text,
   * the same question the estate-administration intake form asks (VALE-1402).
   */
  willStorageLocation?: string;
  deathCertificateIssued?: LeadTriState;
  /** A matter-type slug. Defaults to `estate-administration` when omitted. */
  enquiryType?: string;
  businessSource?: LeadBusinessSource;
  /** The referring person or firm — free text, distinct from the channel. */
  referredBy?: string;
  /** Notes from the call. Surfaces as the drawer's Notes card. */
  moreInfo?: string;
  /** Who took the call. Omitted ⇒ the backend stamps the authenticated caller. */
  ownerUserId?: number;
  /**
   * The firm contact the coordinator picked as the enquirer from the form's
   * suggestions (VALE-2074). The enquirer is that contact, and anything typed
   * over it is written to it. Omitted, the backend resolves the enquirer's
   * contact from the details.
   */
  contactId?: number;
}

// =============================================================================
// Duplicate awareness — `GET /leads/duplicates`
// =============================================================================

/**
 * The shortest term the duplicate search acts on, mirroring the backend's
 * `LEAD_DUPLICATE_MIN_TERM_LENGTH`.
 *
 * Restated here so the modal can skip the request entirely below the threshold
 * rather than firing one per keystroke that it knows will come back empty. The
 * backend applies the same floor regardless — this is an optimisation, never
 * the enforcement.
 */
export const LEAD_DUPLICATE_MIN_TERM_LENGTH = 3;

/** Which supplied term matched, so the warning can say *why* a row is a candidate. */
export type LeadDuplicateMatchField = 'clientNames' | 'deceasedName';

/** One possible duplicate of the enquiry being captured. */
export interface LeadDuplicateCandidate {
  id: number;
  clientNames: string;
  deceasedName: string | null;
  /** ISO-8601 instant — when the existing enquiry came in. */
  enquiryAt: string;
  /**
   * `null` while still in progress. A **closed** lead is still worth warning
   * about: the same family ringing back months later is a re-enquiry, which is
   * exactly the case worth spotting.
   */
  outcome: LeadOutcome | null;
  /** Both entries when the enquirer and the deceased both matched. */
  matchedOn: LeadDuplicateMatchField[];
}

/**
 * The duplicate-awareness result — **a read that warns, and never blocks**
 * (R1.6, and `functional-spec.md` §5.1 says so twice).
 *
 * `POST /leads` does not consult it and cannot fail because of it: the same
 * family genuinely does ring twice, and a re-enquiry is a legitimate second
 * lead. Empty when neither term reached the minimum length.
 */
export interface LeadDuplicatesResponse {
  /** Newest enquiry first, capped at 5 by the backend. */
  candidates: LeadDuplicateCandidate[];
  /**
   * The firm's contacts the enquirer may be (VALE-2074): a name match, or an
   * exact email or phone match (those first). Capped at 5.
   */
  contacts: LeadContactSuggestion[];
}

/** One of the firm's contacts the enquirer being captured might be. */
export interface LeadContactSuggestion {
  id: number;
  name: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  preferredName: string | null;
  email: string | null;
  phoneNumber: string | null;
  address: PostalAddress | null;
}

// =============================================================================
// Lead detail — `GET /leads/:id`, and the `POST /leads` response
// =============================================================================

/**
 * Every derived fact, resolved server-side by `LeadStageService`.
 *
 * **The frontend renders this and re-derives none of it** — the single most
 * important structural rule in the module.
 */
/**
 * Where an enquiry stands in the allocation hand-over, settled server-side.
 *
 * Four facts, each answering a question the other three cannot: *does this firm
 * hand enquiries over at all*, *has this one been handed over*, *was anybody
 * actually reached*, and *has somebody taken it on*. Between them they are
 * enough to write the sentence the surface shows, which is the whole reason the
 * block exists — the band shipped with nothing anywhere a user could read.
 */
export interface LeadAllocationState {
  /**
   * Whether the firm has named anybody under Allocation handover.
   *
   * **`false` means the workflow is off for this firm**: render no allocation
   * chrome at all — no hand-over button, no status line — exactly as the board
   * omits the section. That is what keeps a firm whose solicitors run their own
   * matters seeing what it saw before.
   */
  configured: boolean;
  /**
   * When somebody handed this enquiry over, or `null` if nobody has — or it was
   * withdrawn. **Non-null is exactly the `pending_allocation` band's entry
   * condition**, so this and `derived.section` cannot disagree.
   */
  requestedAt: string | null;
  /**
   * When the firm's allocator(s) were last actually emailed.
   *
   * Deliberately distinct from `requestedAt`: a hand-over whose recipients all
   * have email notifications off is still a hand-over, and claiming they were
   * told would answer *has she seen this yet?* with a falsehood. **Read it
   * paired with `requestedAt`** — an instant left over from a withdrawn
   * hand-over is history, not a live state.
   */
  notifiedAt: string | null;
  /**
   * The acting solicitor on the enquiry's matter, or `null`. Non-null **is** the
   * hand-over's completion. The matter's solicitor, never the enquiry's
   * `preferredSolicitorId`, which records who the enquirer asked for.
   */
  allocatedSolicitorId: number | null;
}

export interface LeadDerivedState {
  effectiveStage: LeadEffectiveStage;
  /** Whole **elapsed** days in the current stage. Never negative. */
  daysInStage: number;
  isStalled: boolean;
  hasInboundReply: boolean;
  hasUnseenReply: boolean;
  /** Cleared by the Documents card, through `POST /leads/:id/documents/mark-seen`. */
  hasUnseenClientDocuments: boolean;
  costAgreementState: LeadCostAgreementState;
  /** A restatement of `effectiveStage === 'ready_to_book'`. No badge, and no ordering. */
  isReadyToBook: boolean;
  /** `null` once the lead has an outcome and belongs to a terminal tab. */
  section: LeadSection | null;
}

/**
 * Someone attending a meeting **besides the enquirer**. Greeted in the booking
 * confirmation, and sent it when they have an address.
 */
export interface LeadMeetingAttendee {
  firstName: string;
  lastName: string | null;
  email: string | null;
}

/** One of a lead's 0..N meetings. */
export interface LeadMeeting {
  id: number;
  startsAt: string;
  /**
   * Whether this meeting has already happened, graded server-side against the
   * **single instant** the whole read derived against.
   *
   * Published rather than compared client-side for the same reason the board
   * row's `nextMeetingIsPast` is: the drawer's narrative, its Activity timeline
   * and the row all say whether a meeting has been, and a browser-clock
   * comparison could make them disagree with each other and with the ordering
   * the row was placed in.
   */
  isPast: boolean;
  bookedSolicitorId: number | null;
  preference: LeadMeetingPreference | null;
  /** Only meaningful when `preference` is `video`. */
  link: string | null;
  /** The firm's office an in-person meeting is at. `null` when none is recorded. */
  officeId: number | null;
  /** Everyone attending besides the enquirer. */
  attendees: LeadMeetingAttendee[];
  /** A note for the solicitor. Never sent to the client. */
  note: string | null;
  /** `null` for a meeting recorded silently, which is legitimate. */
  confirmationSentAt: string | null;
  briefSentAt: string | null;
  recordingId: number | null;
  /**
   * How long the meeting is booked for, in minutes, one of
   * `LEAD_MEETING_DURATION_OPTIONS_MINUTES`. `null` for a meeting booked before
   * the duration existed, treated as the default hour.
   */
  durationMinutes: number | null;
  /**
   * Where the meeting's Outlook calendar invitation stands (CAL-3). `null` for a
   * meeting booked before calendar sync existed; `off` when sync did not carry it.
   */
  calendarState: LeadMeetingCalendarState | null;
}

/** `LeadMeetingDto.calendarState`, the backend's `OutlookCalendarEventState`. */
export type LeadMeetingCalendarState =
  | 'pending'
  | 'written'
  | 'failed'
  | 'removed_in_outlook'
  | 'orphaned'
  | 'off';

/** Something the firm is waiting on the client for. **Advisory only** — never gates a stage. */
export interface LeadOutstandingItem {
  id: number;
  type: LeadOutstandingItemType;
  note: string | null;
  requestedAt: string;
}

/**
 * The latest cost agreement — **latest wins**, so the card reads this alone.
 *
 * **This, not `derived.costAgreementState`, is what separates the card's first
 * two states.** The board's state enum reports `none` for *both* "nothing
 * drafted" and "drafted, not sent", deliberately: a row's question is "what do
 * I do next", and for a draft the answer is still "send it". The card asks a
 * different question, so it reads presence (`null` vs a row) and `sentAt`
 * (§4.10). Neither is a re-derivation — both are stored facts the server sent.
 */
export interface LeadCostAgreement {
  id: number;
  source: LeadCostAgreementSource;
  /**
   * Who drafted it — the `who` in `Generated · Jane Smith, 2 days ago`,
   * resolved against the firm roster the surface already holds. `null` for a row
   * whose author has since been removed.
   */
  addedByUserId: number | null;
  addedAt: string;
  /** `null` while still drafted. */
  sentAt: string | null;
  signedAt: string | null;
  /**
   * The unsigned PDF that will go out. Produced at **draft** time (LEADS-44), so
   * it exists from the moment the agreement does — which is what lets the send
   * dialog's review step name the real document rather than describe one.
   */
  fileId: number | null;
  /** That document's filename. `null` is rendered as absent, never as a guess. */
  documentName: string | null;
  /** The executed copy. `null` until the completion webhook lands. */
  signedFileId: number | null;
  signedDocumentName: string | null;
  /**
   * Whether this firm can amend the agreement in Word in place (LF-6159).
   *
   * **The single gate for the whole affordance.** It is `true` only when the
   * firm has active Microsoft 365 consent. Most firms will not have it,
   * which is why the absent case is the card's ordinary state rather than a
   * fallback.
   */
  wordEditingAvailable: boolean;
  /**
   * When the last Word save-back landed; `null` if never edited in Word.
   *
   * Drives the "Synced from Word N min ago" line and, with the field below, the
   * provenance line — which must name whoever produced the version that goes
   * out, not whoever first drafted it.
   */
  lastSyncedFromWordAt: string | null;
  /**
   * Who was holding the Word checkout when that edit landed. `null` when never
   * edited in Word, or when that colleague has since left the firm.
   */
  lastEditedInWordByUserId: number | null;
  /**
   * A Word session is open on this agreement right now.
   *
   * Gates the card's manual refresh and its automatic pull on returning from
   * Word: outside a live session the read seam is a local no-op, so both would
   * do nothing. Paired with `lastSyncedFromWordAt` it also names the state the
   * card used to render as an unchanged provenance line — opened in Word,
   * nothing back yet — which is exactly when the solicitor is about to send the
   * client something to sign (VALE-288).
   */
  wordCheckoutOpen: boolean;
  /**
   * Which optional merge fields came back empty when this agreement was
   * drafted (VALE-788). Advisory: the agreement exists and can be sent.
   */
  missingOptionalFields: string[];
}

/**
 * The Word working copy — `POST /leads/:id/cost-agreement/edit-in-word`
 * (LF-6159).
 *
 * Same shape as every other Edit-in-Word response in the product, because the
 * caller does the same thing with all of them: point the already-open tab at
 * `webUrl`.
 */
export interface EditLeadCostAgreementInWordResponse {
  costAgreementId: number;
  /** Derived fresh every call — there is no cached session URL. */
  webUrl: string;
  /** An existing working copy was reopened rather than newly provisioned. */
  reopened: boolean;
  /**
   * Why the per-user SharePoint invite failed, or `null` when it landed.
   * **Non-fatal** — the copy exists and the URL is valid — but a non-null value
   * must be surfaced via `wordInviteFailureToast`, or this user lands on a bare
   * access-denied page with no explanation.
   */
  inviteFailure: SpeInviteFailureReason | null;
}

/**
 * One costs-agreement precedent the Configure step's picker offers —
 * `GET /leads/:id/cost-agreement/precedents` (§5.5).
 *
 * **Not the templates library's `DocumentTemplateEntry`.** Both costs-agreement
 * types are filtered out of that list for every firm without
 * `experimental-precedent-types`, which a leads pilot firm has no reason to hold
 * — so a picker fed from it would be empty on exactly the firms this ships to.
 * The lead-scoped route is gated on `leads` instead.
 *
 * It returns the firm's own templates of **either** costs-agreement type, plus
 * Vale's `enquiry-costs-agreement` standard. The matter-stage Vale standard is
 * deliberately absent: it recites the required `<<lpr.names>>` and no executor
 * exists before a grant, so offering it would offer a door that 409s after a
 * spinner. That never applied to a standard built on the enquiry's own fields,
 * which is why one now appears.
 *
 * **Exactly one entry carries `isDefault`.** The server resolves it, by the same
 * precedence drafting-with-no-choice uses, so the picker's opening selection
 * cannot disagree with what would actually be rendered.
 */
export interface LeadCostAgreementPrecedent {
  /** The firm's own template, or `null` for a Vale standard. */
  templateId: number | null;
  /** The Vale standard, or `null` for one of the firm's own. */
  systemKey: string | null;
  /**
   * Which costs agreement this is. `enquiry-costs-agreement` needs no matter;
   * `costs-agreement` is the matter-stage type, offered only because a firm may
   * already have authored a lead-ready agreement under it.
   */
  documentType: DocumentType;
  name: string;
  /** What the picker opens on — exactly one entry in the list. */
  isDefault: boolean;
}

/**
 * Draft a cost agreement — `POST /leads/:id/cost-agreement` (§4.10's two doors).
 *
 * Rides `multipart/form-data` on both doors, because the upload half attaches a
 * `.docx`; the precedent half simply sends no file. The client assembles the
 * `FormData`, so callers pass this shape.
 */
export interface CreateLeadCostAgreementInput {
  source: LeadCostAgreementSource;
  /**
   * Which of the firm's own precedents to render — from either costs-agreement
   * type. Mutually exclusive with `systemKey`. Ignored on the `upload` door.
   */
  templateId?: number;
  /**
   * Render a Vale standard instead — the `systemKey` from the precedent list.
   * Only the enquiry-stage standard is accepted; the matter-stage one cannot be
   * produced before a matter exists. Mutually exclusive with `templateId`.
   *
   * Omitting both resolves the firm's default (enquiry-stage first, then
   * matter-stage), falling back to the Vale enquiry standard.
   */
  systemKey?: string;
  /** The solicitor's own `.docx`, on the `upload` door. Must contain `<<signatureBlock>>`. */
  file?: File;
}

/**
 * Send the drafted agreement for signature —
 * `POST /leads/:id/cost-agreement/send`.
 *
 * Deliberately tiny: everything the send needs is already on the drafted row.
 * The one thing a caller legitimately varies is *who* it goes to, because the
 * enquirer's email is frequently corrected on the call that follows the meeting.
 */
export interface SendLeadCostAgreementInput {
  /** Send here instead of the address on the enquiry. Omitted uses the enquiry's own. */
  recipientEmail?: string;
}

/**
 * The lead's current intake form, as the Status narrative and the Intake form
 * card read it (`functional-spec.md` §4.7, §4.8).
 *
 * Presence, progress and identity — never the form's contents, which are the
 * `Intake form` tab's own read.
 *
 * `status` reuses the shipped `IntakeFormStatus` union rather than restating the
 * seven members: it is the same column, and a second copy is a map that goes
 * stale the first time a status is added.
 */
export interface LeadIntakeFormSummary {
  /** The form's UUID — what `POST /intake-forms/:id/resend` takes. */
  id: string;
  status: IntakeFormStatus;
  /**
   * How full the form is, 0–100. The single server-side definition the board
   * row's Form column and the instruction-sheet download's own gate both read,
   * so the card can never offer a download the endpoint then refuses.
   */
  completionPercent: number;
  /** ISO-8601 — when the invitation went out. */
  requestedAt: string;
  /** How many documents the client has attached to the form. Listed on the Documents card. */
  documentCount: number;
  /**
   * **The address the invitation is actually addressed to**, the intake form's
   * own recipient contact, *not* `Lead.email` (VALE-1615).
   *
   * The two legitimately diverge: the recipient is resolved once, at send time,
   * so editing the enquiry's email afterwards does not move the outstanding
   * invitation. Read this whenever the question is "where did the form go";
   * `lead.email` answers a different question and answering it here is the
   * defect this field was added to close.
   */
  recipientEmail: string | null;
  /** Display name of the recipient the invitation is addressed to. */
  recipientName: string | null;
}

/** One entry in the append-only activity log. */
export interface LeadEvent {
  id: number;
  type: LeadEventType;
  /** When it **happened** — not when the row was written. */
  occurredAt: string;
  /** `null` for a system-originated event — a scheduled chase, an inbound reply. */
  actorUserId: number | null;
  data: Record<string, unknown> | null;
}

/**
 * One of the firm's offices, as the enquiry's office picker needs it —
 * `LeadOfficeOptionDto`.
 *
 * Id **and** name, because neither alone is enough: the enquiry stores an id and
 * the drawer renders a name, so a list of names could not produce the patch and
 * a list of ids could not be read. Nothing else about an office is published
 * here — its address, phone and timezone are branding no lead surface renders.
 */
export interface LeadOffice {
  id: number;
  name: string;
}

/**
 * A lead's full detail — `LeadDetailDto`.
 *
 * Returned by **both** `GET /leads/:id` and `POST /leads`, which is what lets
 * the capture modal open the new lead without a second request. Typed in full
 * rather than narrowed to the fields capture happens to read: the response
 * genuinely is this shape, and a partial mirror would be a lie the compiler
 * then enforces on every later consumer.
 *
 * Date fields are `string`, not `Date` — JSON carries ISO-8601 text and nothing
 * revives it.
 */
export interface LeadDetail {
  id: number;

  // ── Identity ────────────────────────────────────────────────────────────
  /*
    **The parts are stored; the two composed strings are derived server-side.**

    An inline editor patches a part; everything that *renders* a name reads the
    composed string, which the backend builds with its one display helper. The
    frontend must not compose its own — two surfaces spacing a name differently
    is exactly the class of defect this module's derivation rule exists to stop.
  */
  /** Both enquirers, joined with ` & ` when a second one was captured. Rendered, never edited. */
  clientNames: string;
  /**
   * The **first** enquirer alone, composed server-side — what a row editing that
   * one person's parts renders. Distinct from `clientNames`, which folds both
   * people in and would therefore name the partner on the Enquirer row too.
   */
  clientName: string | null;
  clientFirstName: string;
  clientMiddleName: string | null;
  clientLastName: string | null;
  /** How the enquirer is actually addressed. Supersedes the legal name in email greetings. */
  clientPreferredName: string | null;
  /** The optional second enquirer — the spouse or co-executor who rang in too, composed. */
  client2Name: string | null;
  client2FirstName: string | null;
  client2MiddleName: string | null;
  client2LastName: string | null;
  deceasedName: string | null;
  deceasedFirstName: string | null;
  deceasedMiddleName: string | null;
  deceasedLastName: string | null;
  /** The invitation email's only gender signal. Not asked at capture. */
  deceasedTitle: string | null;
  relationshipToDeceased: string | null;
  /** Whether the enquirer is an LPR (VALE-2061). `unknown` is not asked yet. */
  clientIsLpr: LeadTriState;
  /** The enquirer's LPR role. `null` while not an LPR, or while the role is not known. */
  clientLprRole: LprRole | null;
  /** The people on the enquiry other than the enquirer, in the order added. */
  contacts: LeadContact[];
  /** Executors the intake form names who are not on the enquiry yet. */
  intakeNamedExecutors: LeadNamedExecutor[];
  enquiryType: string;

  // ── Pipeline ────────────────────────────────────────────────────────────
  /** The **stored** stage. Prefer `derived.effectiveStage` for display. */
  stage: LeadStage;
  outcome: LeadOutcome | null;
  notProceedingReason: LeadNotProceedingReason | null;
  notProceedingNote: string | null;
  /** The board's one manual mark. It deliberately does **not** reorder anything. */
  flagged: boolean;
  /**
   * Automated intake-form reminders are suspended for this enquiry (R4.1).
   *
   * Suppresses only the **schedule** — a reminder can still be sent by hand, and
   * the lead derives its stage, stall and ordering exactly as before. Pausing
   * must not become a way to lose a lead quietly, so it is deliberately not an
   * input to the derivation. Drives §4.8's `Pause`/`Resume follow-ups` toggle,
   * and hides `Send a reminder now`, which would otherwise contradict the state
   * shown directly above it.
   */
  chasePaused: boolean;

  // ── The intake owner and the solicitor ───────────────────────────────────
  /**
   * Who took the enquiry — the **intake owner** (LF-6099). Assignable at capture
   * and editable in the drawer, and **never clearable**: erased provenance
   * cannot be recovered, and "who took this call" is a fact rather than a
   * setting. Distinct from `preferredSolicitorId`, and distinct again from the
   * *matter* owner the practice manager sets after conversion — which is why
   * every surface calls this one "Intake owner" and never bare "Owner".
   */
  ownerUserId: number;
  preferredSolicitorId: number | null;
  /**
   * The office handling the enquiry — a **foreign key** into the firm's offices
   * (VALE-540), not a name. `null` means no office is recorded, which is the
   * permanent state for a firm that has none.
   */
  officeId: number | null;
  /**
   * The assigned office's name, **resolved server-side**.
   *
   * Published separately from `offices` because a *deactivated* office still
   * resolves here and is deliberately absent from that list: a closed office must
   * keep displaying on the enquiries already pointing at it while never being
   * offered for a new assignment. So the drawer renders this rather than matching
   * `officeId` against the list — which for a closed office would find nothing
   * and read as "no office recorded".
   */
  officeName: string | null;
  /**
   * The firm's **assignable** offices — active only, in the order the firm set,
   * resolved server-side (VALE-540).
   *
   * Empty ⇒ the firm has no offices and the drawer omits the `Office` row
   * altogether. There is **no free-text fallback**: an office is a record, so one
   * that does not exist cannot be named on an enquiry. The frontend never
   * re-derives or hardcodes this — the module's single most important structural
   * rule, and a firm's real office list is exactly the sort of configuration a
   * client-side guess gets wrong.
   */
  offices: LeadOffice[];

  // ── Attribution ─────────────────────────────────────────────────────────
  businessSource: LeadBusinessSource | null;
  referredBy: string | null;

  // ── Contact ─────────────────────────────────────────────────────────────
  /**
   * The enquirer's firm contact, from capture (VALE-2074). The name, email,
   * phone and address on this enquiry are that contact's, and are edited
   * wherever the person appears.
   */
  contactId: number;
  /** The second enquirer's firm contact, `null` for a solo enquiry. */
  client2ContactId: number | null;
  phone: string | null;
  email: string | null;
  address: PostalAddress | null;

  // ── Estate facts ────────────────────────────────────────────────────────
  firmHoldsWill: LeadTriState;
  /**
   * Where the original will is held: free text, read alongside `firmHoldsWill`
   * (VALE-1402). The Estate card offers the row whenever the answer is not
   * `yes`, and keeps offering it while it holds text, so flipping the answer to
   * `yes` cannot orphan a location somebody recorded.
   */
  willStorageLocation: string | null;
  deathCertificateIssued: LeadTriState;
  /**
   * Whether the firm has run its conflict check (LF-6099).
   *
   * The same tri-state vocabulary as the two facts above, and deliberately
   * **not** an outstanding item: an outstanding item means "waiting on the
   * client", while this is the firm's own task. Who ran it and when is in the
   * activity timeline, written by the backend — this carries the current value
   * alone, and the board shows no signal for it at all.
   */
  conflictCheckStatus: LeadTriState;
  executorCount: number | null;
  /**
   * The other executors and any other party to the estate — free text.
   *
   * Free text rather than a modelled party list because it exists to feed the
   * conflict check, which is run for **all** executors: what the coordinator has
   * on the call is names, and a structured party editor at that moment is the
   * form she abandons.
   */
  otherPartyDetails: string | null;
  /** The **estate's** net value, in cents. Never a float. */
  netAssetValueCents: number | null;
  financialAdviser: string | null;

  // ── Planning facts (estate-planning enquiries) ─────────────────────────
  existingWill: LeadTriState;
  existingPowerOfAttorney: LeadTriState;
  /**
   * The caller's partner, free text — distinct from the structured
   * second-enquirer fields (`client2*`), which name a co-enquirer on this call.
   */
  partnerName: string | null;
  /** Free text — dependants/children the coordinator noted on the call. */
  childrenNote: string | null;

  // ── Free text ───────────────────────────────────────────────────────────
  moreInfo: string | null;

  // ── Linkage ─────────────────────────────────────────────────────────────
  matterId: number | null;
  /**
   * The linked matter's reference, for the detail header's `#REF` chip.
   *
   * On the detail read as well as the board row (LF-5988) because the lead
   * **page** renders the same composition as the drawer from this response
   * alone — it has no board row to inherit the reference from, so leaving it off
   * would force a second source for one chip.
   */
  matterReference: string | null;
  matterLinkSource: LeadMatterLinkSource | null;
  /** A calendar date, not an instant. */
  initialFeePaidDate: string | null;

  // ── Timestamps ──────────────────────────────────────────────────────────
  enquiryAt: string;
  stageChangedAt: string;
  /** Written **only** when the Communications tab is opened. */
  threadLastSeenAt: string | null;
  threadLastSeenById: number | null;
  createdAt: string;
  updatedAt: string;

  // ── Resolved / derived ──────────────────────────────────────────────────
  derived: LeadDerivedState;
  /**
   * Where this enquiry stands in the allocation hand-over — every fact the
   * surface needs to **say** why it is, or is not, awaiting allocation.
   *
   * The band used to have no read-out at all, which is precisely what Coulter
   * Legal reported: an enquiry appeared in `Pending allocation` and nothing
   * anywhere explained it. Render these; never re-derive the state from
   * `events`, which is the module's founding rule and would put a second
   * definition of the hand-over in the browser.
   */
  allocation: LeadAllocationState;
  /** The next upcoming meeting, else the most recent past one. */
  nextMeetingAt: string | null;
  /**
   * Whether an intake form was ever sent — resolved server-side from the
   * activity log, like every other derived fact here.
   *
   * The drawer's `Intake form` tab renders **only** when this is true (§4.1): a
   * walk-in with no form must get two tabs rather than a dead one.
   *
   * The **log**, where `intakeForm` below is the artefact: a form row that
   * exists but was never dispatched has not been sent, and the tab must not
   * appear for it.
   */
  hasIntakeForm: boolean;
  /**
   * The lead's current form — its progress and identity, for the Status
   * narrative's form sentence and the Intake form rail card. `null` when the
   * lead has none, never a zero-percent placeholder: §4.8's no-form state and
   * its 0%-out state are different surfaces.
   */
  intakeForm: LeadIntakeFormSummary | null;
  /**
   * Automated follow-up reminders actually sent — the same figure the board
   * row's bell publishes, settled server-side off the activity log rather than
   * counted in the browser.
   */
  chaseCount: number;
  /** From the latest `closed` event — there is no `closed_at` column. */
  closedAt: string | null;
  meetings: LeadMeeting[];
  /** Uncleared items only. */
  outstandingItems: LeadOutstandingItem[];
  costAgreement: LeadCostAgreement | null;
  /** The activity log, newest first. */
  events: LeadEvent[];
}

// =============================================================================
// Inline field edits — `PATCH /leads/:id`
// =============================================================================

/**
 * A partial field update — `UpdateLeadDto`, and nothing beyond it (LF-5988 /
 * LEADS-21, `functional-spec.md` §4.3–§4.6).
 *
 * **An absent key leaves the column alone; an explicit `null` clears it.** That
 * distinction is the whole contract of a genuinely partial patch, and it is why
 * every clearable field is typed `T | null` rather than merely optional — sending
 * `undefined` and sending `null` mean different things, and TypeScript is what
 * stops a caller conflating them.
 *
 * **The omissions are load-bearing, not an oversight.** The backend enforces
 * this whitelist twice — `UpdateLeadDto` under the global `ValidationPipe`
 * (`whitelist: true, forbidNonWhitelisted: true`, so an undeclared field is
 * **rejected with 400**, never silently dropped) and a compile-time
 * `LeadFieldPatch` type. Absent on purpose:
 *
 * - `stage`, `outcome`, `notProceedingReason`, `notProceedingNote`, `flagged`,
 *   `matterId`, `matterLinkSource` — each is a pipeline transition with its own
 *   audited endpoint, because routing one through a field edit would skip the
 *   `lead_events` row the activity timeline is built from.
 * - `contactId` — set once, at capture (VALE-2074). The enquirer's own
 *   details below are written through to that contact.
 * - `threadLastSeenAt` / `threadLastSeenById` — written only by opening the
 *   Communications tab, which is what gives the unread marker an honest exit.
 *
 * Two fields accept no `null` because they cannot be cleared: `clientNames` is
 * the lead's only required field, and `ownerUserId` records who took the
 * enquiry, and erased provenance cannot be recovered. The drawer's `Intake
 * owner` field sends `ownerUserId` (LF-6099) and offers no "unassign" option,
 * which is that rule expressed in the UI rather than only in this type.
 */
export interface UpdateLeadInput {
  // ── Enquiry card (§4.4) ───────────────────────────────────────────────────
  /** Required on the lead, so it cannot be cleared — an empty value is a 400. */
  clientFirstName?: string;
  clientMiddleName?: string | null;
  clientLastName?: string | null;
  /** How the enquirer is actually addressed. Clearable — cleared falls back to the legal name. */
  clientPreferredName?: string | null;
  /** Clearing all three of these retires the second enquirer from the enquiry. */
  client2FirstName?: string | null;
  client2MiddleName?: string | null;
  client2LastName?: string | null;
  /** The enquirer's relationship **to** the deceased; the email inverts it. */
  clientIsLpr?: LeadTriState;
  clientLprRole?: LprRole | null;
  relationshipToDeceased?: string | null;
  /** A matter-type slug. Changing it moves slug and label together. */
  enquiryType?: string;
  /** Editable because a lead is often typed up after the call it records. */
  enquiryAt?: string;
  /**
   * Which office is handling it — an **id from `LeadDetail.offices`**, never a
   * name (VALE-540). The backend validates it against the caller's own firm, so a
   * foreign id is a 400 rather than a stored value. `null` clears it.
   */
  officeId?: number | null;
  /**
   * Who took the enquiry — the drawer's `Intake owner` field. **Not clearable**:
   * the field offers no "unassign", and the spec here says why.
   */
  ownerUserId?: number;
  preferredSolicitorId?: number | null;
  businessSource?: LeadBusinessSource | null;
  referredBy?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: PostalAddress | null;

  // ── Estate card (§4.5) ────────────────────────────────────────────────────
  deceasedFirstName?: string | null;
  deceasedMiddleName?: string | null;
  deceasedLastName?: string | null;
  /** The invitation email's only gender signal — not decoration. */
  deceasedTitle?: string | null;
  /** The **estate's** net value in **cents**. Never a float. */
  netAssetValueCents?: number | null;
  firmHoldsWill?: LeadTriState;
  /** Where the original will is held: free text, cleared with `null`. */
  willStorageLocation?: string | null;
  deathCertificateIssued?: LeadTriState;
  /** The firm's own conflict check — a mark, never an outstanding item. */
  conflictCheckStatus?: LeadTriState;
  executorCount?: number | null;
  /** The other executors, free text — what the conflict check is run against. */
  otherPartyDetails?: string | null;
  financialAdviser?: string | null;

  // ── Planning card (estate-planning enquiries) ────────────────────────────
  existingWill?: LeadTriState;
  existingPowerOfAttorney?: LeadTriState;
  /** The caller's partner, free text. */
  partnerName?: string | null;
  /** Free text — dependants/children the coordinator noted on the call. */
  childrenNote?: string | null;

  // ── Notes (§4.6) ──────────────────────────────────────────────────────────
  moreInfo?: string | null;

  // ── Intake form card (§4.8) ───────────────────────────────────────────────
  /**
   * Suspend the automated chase schedule (R4.1) — the `Pause`/`Resume
   * follow-ups` toggle.
   *
   * It is editable through the field-patch route, unlike `stage` / `outcome` /
   * `flagged`, precisely because it is a **setting rather than a pipeline
   * transition**: it suppresses the schedule and nothing else, moves the lead
   * nowhere, and so has no `lead_events` row to skip. A reminder can still be
   * sent by hand while paused, and `isStalled` is unaffected — pausing must not
   * become a way to lose a lead quietly.
   */
  chasePaused?: boolean;

  // ── Cost agreement card ───────────────────────────────────────────────────
  /** A calendar date, hand-marked permanently (R8.1). */
  initialFeePaidDate?: string | null;
}

// =============================================================================
// The two terminal decisions and the advisory items — LF-5996 / LEADS-29
// =============================================================================

/**
 * Close an enquiry into one of its two terminal bands — `POST /leads/:id/close`
 * (`functional-spec.md` §5.7, §4.7).
 *
 * **One shape carries both outcomes.** `Mark proceeding` and `Not proceeding…`
 * are the Status card's two decisions and both are closures — each moves the
 * lead out of the In-progress pipeline into a terminal tab, and both must write
 * the `closed` event that *both* terminal tabs are ordered by.
 *
 * **`reason` is mandatory for `not_proceeding` and rejected for `proceeding`.**
 * The server enforces it with a coded 400 rather than dropping it, so the
 * distinction cannot be lost silently.
 *
 * **`note` is optional and persists as `null` when empty** — never `''`, which
 * would later read as "a note was left and it was empty". Sending `undefined`
 * and sending `null` mean the same thing here (there is nothing to clear on a
 * lead that is not yet closed); the type allows both so a caller can be explicit.
 */
export interface CloseLeadInput {
  outcome: LeadOutcome;
  /** One of the seven fixed codes. Required for `not_proceeding` only. */
  reason?: LeadNotProceedingReason;
  /** What the reason code cannot capture. Empty becomes `null` server-side. */
  note?: string | null;
}

/**
 * Record something the firm is still waiting on — `POST /leads/:id/outstanding`
 * (`functional-spec.md` §2.5).
 *
 * **Advisory only**: it never gates a stage, is not a pipeline position, and is
 * recorded and cleared entirely by hand. There is deliberately no source, due
 * date or requester — every one of those would imply an automated origin the
 * product does not have, and nothing links an in-form document upload to an
 * item.
 */
export interface CreateLeadOutstandingItemInput {
  type: LeadOutstandingItemType;
  /** Optional for the two named types, and the whole point of `other`. */
  note?: string | null;
}

/**
 * Set the status by hand — `POST /leads/:id/stage`
 * (`functional-spec.md` §5.6, the manual override).
 *
 * **§2.2's one carve-out.** Status is a read-out, not an input: every other path
 * moves an enquiry by creating the artefact that justifies the stage. This exists
 * for the two cases no artefact can produce — imported data, and a genuine
 * correction — and carries one field for that reason: the `stage_overridden`
 * activity row already records who set it and when, so there is no note and no
 * reason code to invent.
 *
 * `LeadStage` rather than `LeadEffectiveStage` is load-bearing: `ready_to_book` is
 * *derived* from a 100%-complete intake form and never stored, so sending it is a
 * 400. The type is what stops a caller composing one from a rendered
 * `effectiveStage`.
 */
export interface OverrideLeadStageInput {
  stage: LeadStage;
}

// =============================================================================
// Setting a lead up — `POST /leads/:id/setup` and the meeting routes
// =============================================================================

/**
 * The meeting facts both dialogs collect — the mirror of the backend's
 * `LeadMeetingFieldsDto` (LF-5993 / LEADS-26, `functional-spec.md` §5.2–§5.3).
 *
 * **One shape, because there is one form.** The setup dialog's Meeting step and
 * the narrower edit dialog collect exactly these fields, and the prototype
 * duplicated them byte-for-byte — which the backend rejected explicitly, its two
 * request DTOs extending this rather than restating it. The frontend does the
 * same, and `LeadMeetingFields` is the single component bound to it.
 *
 * **`startsAt` is the only required fact.** Vale does not hold the firm's
 * calendar; this records the time agreed on the phone, and everything else about
 * it can legitimately still be unknown when the call ends.
 */
export interface LeadMeetingFieldsInput {
  /** ISO-8601 instant. */
  startsAt: string;
  /** Omitted ⇒ the lead's `preferredSolicitorId`. An explicit `null` is honoured. */
  bookedSolicitorId?: number | null;
  /** Required by the backend whenever a confirmation is going out. */
  preference?: LeadMeetingPreference | null;
  /** Rejected with 400 unless `preference` is `video`. */
  link?: string | null;
  /**
   * One of the firm's offices. Rejected with 400 unless `preference` is
   * `in_person`. Omitted on a create, an in-person meeting takes the enquiry's
   * own office; `null` records none.
   */
  officeId?: number | null;
  /** Everyone attending besides the enquirer. Omitted on an edit, the list is left alone. */
  attendees?: LeadMeetingAttendee[];
  /** A note for the solicitor. Never sent to the client. */
  note?: string | null;
  /**
   * One of `LEAD_MEETING_DURATION_OPTIONS_MINUTES` (CAL-3). Omitted on a create,
   * the backend books the default hour; on an edit an absent key leaves it alone.
   */
  durationMinutes?: number | null;
}

/**
 * `POST /leads/:id/meetings`, and the `meeting` half of a setup call.
 *
 * `sendConfirmation` **defaults to `true`**: the confirmation is the point of
 * recording the time. Unticked, no email goes out at all — a meeting agreed on
 * the phone and recorded silently is a first-class outcome (§5.2), not a
 * degraded one.
 */
export interface CreateLeadMeetingInput extends LeadMeetingFieldsInput {
  sendConfirmation?: boolean;
  /**
   * The intake invitation this same setup is sending **already carries the
   * appointment paragraph**, so no separate booking confirmation goes out.
   *
   * The enquirer used to receive two emails minutes apart that contradicted each
   * other — an invitation saying *"we would need to arrange an appointment"* and
   * a confirmation of one. The meeting is still stamped as confirmed, against the
   * invitation's own correspondence, so nothing later offers to re-send a
   * confirmation the client has already read.
   *
   * Stated by the caller rather than inferred from both halves being present: a
   * draft composed from a firm template the review editor swapped in carries no
   * appointment paragraph and does still need the separate email.
   */
  mergeConfirmation?: boolean;
  /**
   * Send the client a calendar invitation from the firm's Outlook instead of the
   * booking-confirmation email (CAL-3). **Create only**: an edit carrying it is
   * a `400`, because sync is decided at booking and nothing later turns it off.
   * Honoured only when `GET …/meetings/calendar-invitation` says available;
   * otherwise the booking falls back to `sendConfirmation`. Never alongside
   * `mergeConfirmation`, which suppresses the sync.
   */
  syncToCalendar?: boolean;
  /** Written back to the lead. Only needed when the lead has no email yet. */
  recipientEmail?: string | null;
  /**
   * The confirmation **as the sender edited it** in the review step, seeded from
   * `POST /leads/:id/emails/pre-render`. Supplied, the backend dispatches this
   * text as written instead of re-resolving the firm's template — the same
   * lawyer-edited path every other outbound email in the product takes.
   *
   * Omitted, the template renders at send time as before, which is what a re-send
   * with no review step gets. Chips are already resolved and there is no outer
   * wrapper: the wrapper, the review-marker strip and the firm signature are the
   * backend's to apply.
   */
  confirmation?: { subject: string; body: string };
}

/**
 * `PATCH /leads/:id/meetings/:meetingId` — §5.3's narrower edit dialog.
 *
 * Every field is optional: an absent key leaves the column alone, an explicit
 * `null` clears it. **`sendConfirmation` defaults to `false` here, unlike on
 * create** — most edits fix a note or swap the solicitor, and re-sending the
 * whole confirmation for that reads to the client as a second, contradictory
 * booking. Re-confirming a moved appointment is a deliberate tick.
 */
/** `syncToCalendar` on an edit adds (`true`) or removes (`false`) the staff-only Outlook event (CAL-6). */
export type UpdateLeadMeetingInput = Partial<CreateLeadMeetingInput>;

/**
 * `GET /leads/:id/meetings/calendar-invitation`: would a booking made now go
 * out as an Outlook calendar invitation (CAL-3)? The booking dialogs render
 * their Outlook-sync switch only when `available`. A read, never a gate.
 */
export interface LeadMeetingCalendarAvailability {
  available: boolean;
  reason:
    | 'flag_off'
    | 'not_provisioned'
    | 'not_connected'
    | 'reconsent_required'
    | 'no_usable_mailbox'
    | 'calendar_not_granted'
    | null;
  /** The mailbox the invitation would come from. `null` when unavailable. */
  organiserMailboxAddress: string | null;
}

/**
 * What a meeting write hands back — the row **and** the lead's whole refreshed
 * detail, because recording a meeting can move the stage and always changes
 * which meeting the drawer renders.
 */
export interface LeadMeetingWriteResponse {
  meeting: LeadMeeting;
  /** `false` for a silently-recorded meeting, and for an edit that did not re-confirm. */
  confirmationSent: boolean;
  lead: LeadDetail;
}

/**
 * What pushing the pre-meeting brief hands back (§6.2 phase 2).
 *
 * Deliberately not `LeadMeetingWriteResponse`: that carries `confirmationSent`,
 * which means nothing here. The recipient is resolved **server-side** — the booked
 * solicitor, falling back to whoever took the enquiry, so `sentTo` is the only
 * way the toast can honestly name who it reached.
 */
export interface LeadMeetingBriefResponse {
  meeting: LeadMeeting;
  /** The address it went to. Not necessarily the booked solicitor's. */
  sentTo: string;
  lead: LeadDetail;
}

/**
 * One reviewed intake invitation, ready to commit — the same per-draft shape
 * `POST /intake-forms/send-all-invitations` takes, because setup commits through
 * that very service. The drafts are created beforehand by `POST /intake-forms`
 * carrying this lead's `leadId`; what arrives here is the final text.
 */
export interface SetupLeadDraftInput {
  correspondenceId: number;
  subject: string;
  fragments: { mainContent: string };
  /** A full replacement of the draft's attachment set. */
  attachmentFileIds: number[];
  /** Provenance: the firm template composed from, or `null` for Vale's seed. */
  sourceEmailTemplateId?: number | null;
}

/**
 * The deceased block — **captured for the lead, not spent on one email**.
 *
 * Asked in the dialog because this is the moment it changes something visible
 * (the title is the invitation's only gender signal), but written back to the
 * lead, so correcting `Mr` to `Mrs` once fixes every later artefact.
 *
 * **Estate administration only** — supplying it on any other enquiry type is a
 * 400, not a silently-stored field no surface will read.
 */
export interface SetupLeadDeceasedInput {
  /** Three parts, matching what the lead stores and the matter's `decedent*` columns take. */
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  title?: string | null;
  relationship?: string | null;
}

/**
 * `POST /leads/:id/setup` — **one call, three first-class outcomes** (§5.2).
 *
 * Which outcome this is comes from *which halves are present*, not from a mode
 * flag: the entry point decides what is pre-ticked, not what is possible. At
 * least one half must be present; neither is a 400 rather than a call that
 * succeeds having done nothing.
 */
export interface SetupLeadInput {
  /** The enquirer's email, written back to the lead. */
  recipientEmail?: string | null;
  /** Estate-administration enquiries only. */
  deceased?: SetupLeadDeceasedInput;
  /** Present to send the intake invitation. Omit for a meeting-only setup. */
  intakeForm?: { perDraft: SetupLeadDraftInput[] };
  /** Present to record the appointment. Omit for a form-only setup. */
  meeting?: CreateLeadMeetingInput;
}

/** The setup call's response — the contract the dialog closes onto. */
export interface SetupLeadResponse {
  /** The lead, re-read after every write. */
  lead: LeadDetail;
  intakeForm: { sentCorrespondenceIds: number[] } | null;
  meeting: { meeting: LeadMeeting; confirmationSent: boolean } | null;
  /**
   * The stage the lead moved **to**, or `null` when it did not move.
   *
   * `null` is a real outcome, not an absence: a late-captured meeting on an
   * already-met lead correctly leaves the pipeline alone, which is otherwise
   * indistinguishable from "nothing happened".
   */
  stageMovedTo: LeadStage | null;
}

// =============================================================================
// Convert / link-matter — `POST /leads/:id/convert`, `POST /leads/:id/link-matter`
// (LF-6021 / LEADS-50, `functional-spec.md` §5.8, `technical-spec.md` §4, §11)
// =============================================================================

/**
 * Deceased name confirmed by the lawyer — the mirror of the backend's
 * `ConfirmedDeceasedNameDto`. Only ever sent on the inline retry after a
 * `422 DECEASED_NAME_REQUIRED`, never on the first attempt.
 */
export interface ConfirmedDeceasedName {
  firstName: string;
  lastName: string;
}

/**
 * Convert body — deliberately near-empty on the first attempt. One click is
 * the whole interaction (R9.1); `confirmedDeceasedName` exists only for the
 * retry.
 */
export interface ConvertLeadInput {
  confirmedDeceasedName?: ConfirmedDeceasedName;
  /**
   * The firm's own reference for the matter this convert creates, written to
   * `matters.lawFirmInternalReference`.
   *
   * **Named for the lead surface, not the matter column.** The convert response
   * and both lead read surfaces already publish this value as `matterReference`,
   * so the request that sets it uses the same word; the backend maps it onto the
   * matter's column. Asked at convert because the coordinator opens the matter
   * in the firm's own practice-management system in the same act, which is the
   * one moment the reference is in hand.
   */
  matterReference?: string;
  /**
   * The solicitor who will run the matter, set as its **acting solicitor** in
   * the same act as creating it.
   *
   * This is the allocation hand-over's exit. On a firm that names allocators the
   * person converting is typically the allocator — somebody handed the enquiry to
   * them precisely so they would pick it up — and *create the matter* and *decide
   * who runs it* are one decision made in one sitting. Splitting them across two
   * screens is what left new matters sitting unowned.
   *
   * **Optional, and its absence is a real answer**: an allocator who has not
   * decided converts without it, the matter is created unallocated, and the
   * enquiry stays in `Pending allocation` where it honestly belongs.
   */
  actingSolicitorId?: number;
}

/** `POST /leads/:id/link-matter` — associate an existing matter (R9.5). */
export interface LinkLeadMatterInput {
  matterId: number;
}

/** Which artefact kind a convert/link bind result describes. */
export type LeadConvertArtefactKind = 'intake-form' | 'recording';

/**
 * One artefact's bind outcome — the mirror of `LeadConvertArtefactDto`.
 *
 * Binds are **best-effort**: the matter already exists by the time they run,
 * so a refusal is reported here rather than failing the whole call. An
 * unbound artefact is a **legal intermediate state**
 * (`technical-spec.md` §4 rule 4), not an error.
 */
export interface LeadConvertArtefact {
  type: LeadConvertArtefactKind;
  /** A UUID for an intake form, an integer (as a string) for a recording. */
  id: string;
  bound: boolean;
  /** Why the bind was skipped. `null` on success. */
  skippedReason: string | null;
}

/**
 * The response of both `POST /leads/:id/convert` and `POST /leads/:id/link-matter`
 * — the mirror of `LeadConvertResponseDto`. One shape for both: the caller's
 * next action is the same either way — show the matter chip and offer
 * `Open matter`.
 *
 * **Not a `LeadDetail`.** The caller must re-read (or invalidate) the lead to
 * get the refreshed `matterId` / `matterReference` / `derived` state onto the
 * board and drawer — this response exists to drive the immediate confirmation
 * UI (the matter reference, the per-artefact bind report), not to replace a
 * cache entry.
 */
export interface LeadConvertResult {
  leadId: number;
  matterId: number;
  matterReference: string | null;
  matterLinkSource: LeadMatterLinkSource;
  artefacts: LeadConvertArtefact[];
  /** The lead's outcome as stored — unaffected by either route (R9.0). */
  outcome: LeadOutcome | null;
}

/**
 * The lead a matter converted or linked from — `GET /leads/by-matter/:matterId`
 * (LF-6021 / LEADS-50), the mirror of `LeadOriginDto`. Drives the matter
 * overview's "origin lead" chip / deep link back.
 */
export interface LeadOrigin {
  leadId: number;
  clientNames: string;
  deceasedName: string | null;
}

/**
 * `POST /leads/:id/emails/pre-render` — render one of the lead's Vale emails for
 * the review step, sending and persisting nothing (§5.2).
 *
 * **Only the booking confirmation needs this.** The review step's other email,
 * the intake invitation, is a real `INVITATION_DRAFT` correspondence by the time
 * the step opens, so it goes through the shipped form-scoped pre-render that
 * `DraftReviewPanel` already drives. The confirmation has no draft at all — it
 * is rendered and dispatched in one call at send time — which is why the review
 * step previously had nothing to call for it.
 */
export interface PreRenderLeadEmailInput {
  /** Restricted backend-side; anything else is a 400 rather than a broken preview. */
  emailType: EmailType.LEAD_MEETING_CONFIRMATION;
  /** The meeting as the dialog currently holds it. Nothing here is persisted. */
  meeting: LeadMeetingFieldsInput;
  /** The address as typed, before the send writes it back to the lead. */
  recipientEmail?: string;
}

/** One `<mark data-review>` span the template author left for a human to fill. */
export interface LeadEmailReviewMarker {
  location: { startOffset: number; endOffset: number };
  note: string | null;
}

export interface PreRenderedLeadEmail {
  emailType: EmailType;
  resolvedSubject: string;
  /** Review markers are left **intact** — the review gate renders them (§6.1). */
  resolvedBodyHtml: string;
  /** `system` means Vale's seed wording is what will go out. */
  source: 'firm' | 'system';
  templateId: number | null;
  reviewMarkers: LeadEmailReviewMarker[];
  /** Chips that resolved to nothing — §6.1 hard-blocks the send on these. */
  unresolvedMergeTokens: string[];
  /**
   * Every placeholder this render can contain, as its bracketed text
   * (`[office address]`): a fact the email names that Vale has no value for. In
   * the body each is a required review marker; the subject carries no markup, so
   * this is what the review step looks for there.
   */
  requiredPlaceholders: string[];
  attachments: {
    fileId: number;
    position: number;
    filename: string;
    mimeType: string | null;
  }[];
}

// =============================================================================
// Client-supplied documents — LF-6029 / LEADS-53, `functional-spec.md` §4.2
// =============================================================================

/**
 * Who put a document on the enquiry: the firm, scanning it onto the enquiry
 * (removable), or the client, attaching it to the intake form (read-only here).
 */
export type LeadDocumentSource = 'enquiry' | 'intake_form';

/**
 * One document the client has provided on the enquiry, scanned onto it by the
 * firm or attached to the intake form by the client — mirrors the backend
 * `LeadDocumentDto`.
 *
 * Deliberately thin, and carries nothing AI-derived: a lead never runs
 * extraction (that needs a matter), so a document-type or confidence field here
 * would be a promise the pre-matter path cannot keep.
 */
export interface LeadDocument {
  fileId: number;
  originalFilename: string | null;
  fileSizeKb: number | null;
  /** Auto-detected from the filename on upload — how a scanned original is named. */
  isCertifiedCopy: boolean;
  uploadedAt: string;
  source: LeadDocumentSource;
}
